import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { L } from 'harmony-language';
import { useSnackbar } from '../../../components/notifications/use-snackbar';
import { Load, OrganizationCarrier } from '../../../types';
import { loadCellIsEditable } from '../../../components/shared/load-table/utils/load-utils';
import { OrderStatus } from 'harmony-constants';
import { useUser } from '../../queries/use-user';
import { useLoadsKey } from '../../config-hooks';

type CarrierMutationOptions = {
    loads: Load[];
    carrier?: OrganizationCarrier;
}

async function assignCarrier(options: CarrierMutationOptions) {
    const updatedLoads = options.loads.map(load => {
        // before carrier had load settings (who edits driver/trailer/tractor) we just removed the value for those fields in the api.
        // example: now if an owning org can (as either CarrierEditLoadOption.Carrier or CarrierEditLoadOption.both) assign driver and currently have one of their drivers assigned to the load
        //          the driver should not be removed as the currently assigned driver is valid.
        if (options.carrier) {
            const incommingCarrierId = options.carrier.carrierOrganizationId;
            const isBecomingCarrierLoadOrAlreadyThisCarrier = load.organizationId === load.transportingOrganizationId || load.transportingOrganizationId === incommingCarrierId;
            const isAlreadyCarrierLoadAndBecomingDifferentCarrierLoad = load.organizationId !== load.transportingOrganizationId && load.transportingOrganizationId !== incommingCarrierId;

            let transportedByUserId = null, tractorId = null, trailerId = null;
            if (isBecomingCarrierLoadOrAlreadyThisCarrier || isAlreadyCarrierLoadAndBecomingDifferentCarrierLoad) {
                //check status on loadDriver loadTractor loadTrailer
                if (loadCellIsEditable('loadDriver', options.carrier, false)) {
                    transportedByUserId = load.transportedByUserId
                }
                if (loadCellIsEditable('loadTrailer', options.carrier, false)) {
                    trailerId = load.trailerId;
                }
                if (loadCellIsEditable('loadTractor', options.carrier, false)) {
                    tractorId = load.tractorId;
                }
            }

            return {
                id: load.id,
                status: transportedByUserId ? OrderStatus.Assigned : OrderStatus.Open,
                transportedByUserId: transportedByUserId,
                tractorId: tractorId,
                trailerId: trailerId,
                transportingOrganizationId: options.carrier.carrierOrganizationId
            };
        } else {
            //carrier unassign
            return {
                id: load.id,
                status: OrderStatus.Open,
                transportedByUserId: null,
                tractorId: null,
                trailerId: null,
                transportingOrganizationId: load.organizationId,
            }
        }
    });

    return (await axios.post(`${API_BASE}/api/carriers/assign-loads`, updatedLoads)).data;
}

export const useAssignCarrierMutation = () => {
    const snackbar = useSnackbar();
    const { user } = useUser();
    const queryClient = useQueryClient();
    const queryKey = useLoadsKey();

    const mutation = useMutation({
        mutationFn: assignCarrier,
        onMutate: (options: CarrierMutationOptions) => {
            const { loads, carrier } = options;

            queryClient.cancelQueries(queryKey);
            const previousCache = queryClient.getQueryData<Load[]>(queryKey);

            if (previousCache) {
                const newLoads = previousCache.map((load: Load) => {
                    if (loads.find(l => l.id === load.id)) {
                        return {
                            ...load,
                            acceptedByUserId: user.id,
                            transportingOrganizationId: carrier ? carrier.carrierOrganizationId : load.organizationId,
                        };
                    }
                    return load;
                });

                queryClient.setQueryData(queryKey, newLoads);
            }

            const snackbarString = carrier ? `${carrier.carrier?.name}` : (user.organization?.name || '')
            snackbar(L.assignedSnackbarSuccess(snackbarString));

            return previousCache
        },
        onError: (_1, _2, context) => {
            if (context) {
                queryClient.setQueryData<Load[]>(queryKey, context);
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries(queryKey);
        },
    });

    return mutation;
};
